import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SmoothScroll } from "@/components/site/SmoothScroll";
import { Navbar } from "@/components/site/Navbar";
import { Contact, Footer } from "@/components/site/Contact";
import { Eyebrow, Reveal } from "@/components/site/Reveal";
import { PRODUCTS } from "@/data/content";
import { scrollToId } from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];

const VALUES = [
  { t: "Straight talk", d: "Clear terms, plain language and no surprises. You'll know what you're signing and what it costs before you commit." },
  { t: "Speed that matters", d: "Opportunities and emergencies don't wait. We keep the process short so you can move when it counts." },
  { t: "The right fit", d: "Seven products means we don't have to force one. We match the funding to your revenue, timeline and goals." },
  { t: "Partners, not processors", d: "You get a dedicated specialist who knows your file and stays with you after funding." },
];

const PROMISE = [
  ["01", "Your eligibility check won't affect your credit score."],
  ["02", "A real person reviews every application."],
  ["03", "We explain every offer before you sign."],
];

const Line = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${className}`} initial={{ y: "110%", rotate: 3 }} animate={{ y: "0%", rotate: 0 }} transition={{ duration: 1.1, delay: 0.15 + i * 0.13, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);

const AboutHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);
  return (
    <section ref={ref} data-testid="about-hero" className="relative overflow-hidden pb-16 pt-32 lg:pt-40">
      <div className="grid-lines absolute inset-0 [mask-image:radial-gradient(ellipse_at_20%_10%,#000_20%,transparent_70%)]" />
      <div className="absolute -right-40 top-10 h-[480px] w-[480px] rounded-full bg-[var(--sky)]/20 blur-[120px]" />
      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
          <Eyebrow>About 613 Advance</Eyebrow>
        </motion.div>
        <div className="mt-6 grid grid-cols-1 items-end gap-10 lg:grid-cols-12">
          <h1 data-testid="about-title" className="font-display text-[clamp(2.6rem,7vw,6rem)] font-black italic leading-[0.95] tracking-[-0.03em] lg:col-span-8">
            <Line i={0}>We back the</Line>
            <Line i={1}>businesses</Line>
            <Line i={2} className="text-brand-gradient pr-2">that build.</Line>
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8, ease: EASE }} className="text-base leading-relaxed text-[var(--ink)]/75 md:text-lg lg:col-span-4">
            613 Advance helps small and mid-sized businesses get the capital they need to hire, stock up, expand and get through slow seasons, without the runaround.
          </motion.p>
        </div>
        <motion.div initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ duration: 1.3, delay: 0.5, ease: EASE }} className="mt-14">
          <div className="relative h-[280px] overflow-hidden rounded-[28px] sm:h-[420px] lg:h-[520px] [clip-path:polygon(0_0,100%_0,100%_82%,90%_100%,0_100%)]">
            <motion.img style={{ y }} src="/img/handshake.jpg" alt="A 613 Advance specialist shaking hands with a business owner" className="absolute left-0 top-[-12%] h-[130%] w-full object-cover" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,transparent_25%,rgba(6,26,61,0.55)_90%)]" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Story = () => (
  <section data-testid="about-story" className="relative py-24 lg:py-32">
    <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-5 sm:px-8 lg:grid-cols-12">
      <Reveal className="lg:col-span-6">
        <Eyebrow>Why we exist</Eyebrow>
        <h2 className="mt-5 font-display text-4xl font-black italic leading-[1] tracking-tight sm:text-5xl">
          Good businesses deserve a <span className="text-brand-gradient">faster yes.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.1} className="space-y-6 text-base leading-relaxed text-[var(--ink)]/75 md:text-lg lg:col-span-6">
        <p>Traditional lenders often move slowly and look at a narrow set of numbers. For a business owner with payroll due, inventory to buy or a big contract to fulfill, waiting weeks for an answer isn't an option.</p>
        <p>613 Advance was built to close that gap. We look at how your business actually performs, give you real options across seven funding products, and move quickly so you can focus on running your company.</p>
        <p>Whether it's your first advance or your fifth, you'll work with people who pick up the phone, explain the terms and treat your business like it matters, because it does.</p>
      </Reveal>
    </div>
  </section>
);

const Values = () => (
  <section data-testid="about-values" className="relative border-t border-[var(--line)] bg-white py-24 lg:py-32">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <Reveal>
        <Eyebrow>What we stand for</Eyebrow>
        <h2 className="mt-5 font-display text-4xl font-black italic leading-[1] tracking-tight sm:text-5xl">How we work.</h2>
      </Reveal>
      <div className="mt-14 border-t border-[var(--line)]">
        {VALUES.map((v, i) => (
          <Reveal key={v.t} delay={i * 0.06} y={18}>
            <div data-testid={`about-value-${i}`} className="group grid grid-cols-1 gap-4 border-b border-[var(--line)] py-8 transition-colors duration-500 hover:bg-[var(--paper)] md:grid-cols-12 md:items-center md:px-4">
              <span className="font-mono text-sm text-[var(--green)] md:col-span-1">0{i + 1}</span>
              <h3 className="font-display text-2xl font-black italic tracking-tight transition-transform duration-500 group-hover:translate-x-2 sm:text-3xl md:col-span-5">{v.t}</h3>
              <p className="text-base leading-relaxed text-[var(--ink)]/70 md:col-span-6">{v.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const OurPromise = () => (
  <section data-testid="about-promise" className="grain relative overflow-hidden bg-[var(--ink)] py-24 text-white lg:py-32">
    <div className="absolute -left-32 top-0 h-[460px] w-[460px] rounded-full bg-[var(--blue)]/40 blur-[140px]" />
    <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-5 sm:px-8 lg:grid-cols-12">
      <Reveal className="lg:col-span-5">
        <Eyebrow light>Our promise</Eyebrow>
        <h2 className="mt-5 font-display text-4xl font-black italic leading-[1] tracking-tight sm:text-5xl">
          No fine print <span className="text-[var(--lime)]">games.</span>
        </h2>
      </Reveal>
      <div className="space-y-4 lg:col-span-7">
        {PROMISE.map(([n, t], i) => (
          <Reveal key={n} delay={i * 0.1} y={18}>
            <div data-testid={`about-promise-${i}`} className="flex items-center gap-6 rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
              <span className="font-mono text-xl font-bold text-[var(--lime)]">{n}</span>
              <p className="font-display text-lg font-bold italic sm:text-xl">{t}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Offer = () => (
  <section data-testid="about-offer" className="relative py-24 lg:py-32">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <Reveal>
        <Eyebrow>What we offer</Eyebrow>
        <h2 className="mt-5 font-display text-4xl font-black italic leading-[1] tracking-tight sm:text-5xl">Seven ways to fund growth.</h2>
      </Reveal>
      <div className="mt-12 flex flex-wrap gap-3">
        {PRODUCTS.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.05} y={12}>
            <button onClick={() => scrollToId("products")} data-testid={`about-product-${p.id}`} className="group flex items-center gap-3 rounded-full border border-[var(--line)] bg-white px-6 py-4 font-display text-base font-bold italic transition-[background-color,color,border-color] duration-300 hover:border-[var(--blue)] hover:bg-[var(--blue)] hover:text-white sm:text-lg">
              {p.name}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </button>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "About Us | 613 Advance";
    return () => { document.title = "613 Advance | Business Funding Is What We Do"; };
  }, []);
  return (
    <div data-testid="about-page" className="relative">
      <SmoothScroll />
      <Navbar />
      <main>
        <AboutHero />
        <Story />
        <Values />
        <OurPromise />
        <Offer />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
